import { Factory, Store, BadgeCheck } from "lucide-react";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { Container, PageHeader, Eyebrow, Cta, SectionHead, ExportBadge } from "../components/ui-kit";
import { ABOUT_TEXT, STUDIO_GALLERY, FACTORY_PHOTOS, CONTACT } from "../mock";

const P = "/images/products";

const TIMELINE = [
  { year: "1993", title: "Kuruluş", text: "Koşan Düğme, İstanbul'da metal tekstil aksesuarları üretimine başladı." },
  { year: "2000'ler", title: "Büyüme", text: "Ürün gamı çıtçıt, kuşgözü, perçin ve toka gruplarıyla genişledi; yerli üretim kapasitesi artırıldı." },
  { year: "2010'lar", title: "Merter Şubesi", text: "Fabrikanın verimli ve modern yapısı, müşteri odaklı servis anlayışıyla Merter şubesiyle güçlendirildi." },
  { year: "Bugün", title: "Sektörde Güçlü Konum", text: "İhracata uygun ürün garantisi sunabilen üretim yapımızla yurt içi ve yurt dışına hizmet veriyoruz." },
];

const PILLARS = [
  { icon: Factory, t: "Yerli Üretim", d: "Kendi tesisimizde pres, kalıp ve kaplama süreçlerinin tamamını yönetiyoruz." },
  { icon: BadgeCheck, t: "Kalite Kontrol", d: "Üretimin her aşamasında kalite kontrol; ihracata uygun ürün garantisi." },
  { icon: Store, t: "Merter Şubesi", d: "Güngören fabrikamıza ek olarak Merter şubemizle müşterilerimize hızlı servis veriyoruz." },
];

const About = () => {
  return (
    <main className="bg-canvas" data-testid="about-page">
      <Seo
        title="Hakkımızda | 1993'ten Beri Metal Aksesuar Üretimi — Koşan Düğme"
        description="Koşan Düğme, 1993'ten bu yana Güngören fabrikası ve Merter şubesiyle metal tekstil aksesuarları üretiyor. İhracata uygun ürün garantisi ve yerli üretim."
        path="/hakkimizda"
      />
      <Breadcrumbs items={[{ label: "Hakkımızda", to: "/hakkimizda" }]} />
      <PageHeader eyebrow="Kurumsal" title="Hakkımızda" text="1993'ten bu yana, modanın ve işlevselliğin buluştuğu noktada üretiyoruz.">
        <div className="hidden lg:flex items-center justify-end">
          <ExportBadge />
        </div>
      </PageHeader>

      {/* Metin + görsel */}
      <section className="py-20 lg:py-28">
        <Container className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <Reveal>
              <h2 className="font-display font-light text-[2rem] sm:text-[2.6rem] leading-[1.1] tracking-[-0.01em] text-ink">
                Modanın ve işlevselliğin <span className="font-medium">buluştuğu noktadayız.</span>
              </h2>
            </Reveal>
            {ABOUT_TEXT.paragraphs.map((p, i) => (
              <Reveal key={i} delay={80 + i * 80}>
                <p className="text-ink2 leading-relaxed mt-5" data-testid={`about-paragraph-${i}`}>{p}</p>
              </Reveal>
            ))}
            <Reveal delay={420}>
              <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-line">
                {[
                  { v: "32+", l: "Yıl deneyim" },
                  { v: "1000+", l: "Ürün çeşidi" },
                  { v: "48", l: "Kaplama rengi" },
                ].map((s) => (
                  <div key={s.l}>
                    <p className="font-display font-light text-3xl sm:text-4xl text-ink leading-none">{s.v}</p>
                    <p className="text-[11px] tracking-[0.18em] uppercase text-mute mt-2.5">{s.l}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:sticky lg:top-32">
            <Reveal>
              <div className="grid grid-cols-5 gap-4">
                <div className="col-span-3 aspect-[3/4] overflow-hidden bg-plate border border-line">
                  <img src={`${P}/studio_15.jpg`} alt="Kot düğme çeşitleri" className="w-full h-full object-cover" />
                </div>
                <div className="col-span-2 flex flex-col gap-4">
                  <div className="flex-1 overflow-hidden bg-plate border border-line">
                    <img src={`${P}/studio_18.jpg`} alt="Stoper modelleri" className="w-full h-full object-cover" />
                  </div>
                  <div className="bg-ink text-white p-5">
                    <p className="font-display font-light text-4xl leading-none">1993</p>
                    <p className="text-[10px] tracking-[0.22em] uppercase text-white/55 mt-2">Kuruluş yılı</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Yaklaşım */}
      <section className="py-20 lg:py-28 bg-sand border-y border-line" data-testid="about-approach">
        <Container>
          <Reveal>
            <SectionHead eyebrow="Yaklaşımımız" title="Üretimden sevkiyata tam kontrol" />
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-x-12 gap-y-10 mt-16">
            {PILLARS.map((b, i) => (
              <Reveal key={b.t} delay={i * 110}>
                <div className="group border-t border-ink/15 pt-6">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold tracking-[0.25em] text-brand">0{i + 1}</span>
                    <b.icon size={22} strokeWidth={1.4} className="text-ink2 group-hover:text-brand transition-colors" />
                  </div>
                  <h3 className="font-display font-medium text-[17px] text-ink mt-6">{b.t}</h3>
                  <p className="text-ink2 text-[14px] leading-relaxed mt-2.5">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Zaman çizelgesi */}
      <section className="py-20 lg:py-28 bg-paper" data-testid="about-timeline">
        <Container>
          <Reveal>
            <SectionHead eyebrow="Yolculuğumuz" title="Otuz yılı aşkın üretim geçmişi" />
          </Reveal>
          <div className="grid md:grid-cols-4 mt-16">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 110}>
                <div className="relative pr-8 pb-8 md:pb-0 group">
                  <div className="flex items-center gap-3">
                    <span className="w-2.5 h-2.5 rounded-full border-2 border-brand bg-paper group-hover:bg-brand transition-colors shrink-0" />
                    <span className="flex-1 h-px bg-line" />
                  </div>
                  <p className="font-display font-light text-3xl text-ink mt-6">{t.year}</p>
                  <h3 className="text-[12px] font-semibold tracking-[0.16em] uppercase text-brand mt-3">{t.title}</h3>
                  <p className="text-[14px] text-ink2 leading-relaxed mt-3 max-w-xs">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Fabrikamız */}
      <section className="py-20 lg:py-28 bg-canvas border-t border-line" data-testid="about-factory">
        <Container>
          <Reveal>
            <SectionHead
              eyebrow="Fabrikamız"
              title="Güngören'deki üretim tesisimiz"
              text="Bina cephemizden showroom'a, pres hattımızdan montaj bölümüne — üretimin her aşaması kendi tesisimizde."
            />
          </Reveal>
          <div className="columns-2 md:columns-3 gap-4 lg:gap-5 mt-14 [column-fill:_balance]">
            {FACTORY_PHOTOS.map((p, i) => (
              <Reveal key={p.label} delay={(i % 3) * 80} className="mb-4 lg:mb-5 break-inside-avoid">
                <figure className="group">
                  <div className="overflow-hidden bg-plate border border-line">
                    <img
                      src={p.image}
                      alt={p.label}
                      loading="lazy"
                      className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      data-testid={`factory-photo-${i}`}
                    />
                  </div>
                  <figcaption className="mt-3 text-[12px] text-ink2 flex items-center gap-2">
                    <span className="text-[10px] font-semibold tracking-[0.2em] text-mute">{String(i + 1).padStart(2, "0")}</span>
                    {p.label}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Galeri */}
      <section className="py-20 lg:py-28 bg-canvas border-t border-line" data-testid="about-gallery">
        <Container>
          <Reveal>
            <SectionHead
              eyebrow="Galeri"
              title="Ürünlerimizden yakın planlar"
              text="Kemer tokalarından ayar köprülerine, kot düğmelerinden çıtçıt parçalarına — tümü kendi üretimimizden."
            />
          </Reveal>
          <div className="columns-2 md:columns-3 gap-5 mt-14 [column-fill:_balance]">
            {STUDIO_GALLERY.map((s, i) => (
              <Reveal key={s.label + i} delay={(i % 3) * 80} className="mb-5 break-inside-avoid">
                <figure className="group">
                  <div className="overflow-hidden bg-plate border border-line">
                    <img
                      src={s.image}
                      alt={s.label}
                      loading="lazy"
                      className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <figcaption className="mt-3 text-[12px] text-ink2 flex items-center gap-2">
                    <span className="text-[10px] font-semibold tracking-[0.2em] text-mute">{String(i + 1).padStart(2, "0")}</span>
                    {s.label}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-paper border-t border-line" data-testid="about-cta">
        <Container className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <Eyebrow>Birlikte üretelim</Eyebrow>
            <h2 className="font-display font-light text-3xl sm:text-4xl text-ink mt-4">Projenizi bizimle konuşun.</h2>
          </div>
          <div className="flex flex-wrap gap-4">
            <Cta to="/iletisim" data-testid="about-cta-contact">Bize ulaşın</Cta>
            <Cta href={CONTACT.phoneHref} variant="ghost" arrow={false} data-testid="about-cta-phone">{CONTACT.phone}</Cta>
          </div>
        </Container>
      </section>
    </main>
  );
};

export default About;
